import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/cancellation")({
  head: () => ({
    meta: [
      { title: "Cancellation Policy — Peopels" },
      { name: "description", content: "How cancellations, refunds and booking protection work on Peopels, operated by Ashers Hub Ltd." },
    ],
    links: [{ rel: "canonical", href: "/cancellation" }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 sm:px-6 lg:px-8">
        <article className="prose max-w-none">
          <h1 className="font-display text-4xl font-bold">Cancellation Policy</h1>
          <p className="mt-2 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-GB")}</p>
          <p className="mt-6">
            This Cancellation Policy is part of the <Link to="/terms" className="underline">Terms of Service</Link>{" "}
            between you and <strong>Ashers Hub Ltd</strong> (company number <strong>17204476</strong>), operator of
            the Peopels platform.
          </p>

          <h2 className="mt-8 text-xl font-semibold">1. Booking protection — why this exists</h2>
          <p>
            Once a Professional has accepted a booking, the Professional may begin allocating time, reserving
            availability and travelling to the job location. Accepted bookings therefore create real obligations
            on both sides, and customers may not cancel accepted bookings without consequence.
          </p>

          <h2 className="mt-8 text-xl font-semibold">2. Customer cancellation charges</h2>
          <p>If a customer cancels an accepted booking without a valid reason, the following charges may apply:</p>
          <ol>
            <li><strong>Platform and service fees are non-refundable.</strong></li>
            <li>
              <strong>Up to 50% of the booking value may be retained</strong> to compensate the Professional and the
              platform for:
              <ul>
                <li>reserved time</li>
                <li>lost work opportunities</li>
                <li>travel expenses</li>
                <li>administrative costs</li>
              </ul>
            </li>
          </ol>

          <h2 className="mt-8 text-xl font-semibold">3. Valid reasons for cancellation</h2>
          <p>
            Cancellations may qualify for a full refund where there is a valid reason. Examples include (without
            limitation):
          </p>
          <ul>
            <li>duplicate booking</li>
            <li>professional unavailable</li>
            <li>safety concerns</li>
            <li>emergency circumstances</li>
            <li>other reasonable situations reviewed by Peopels</li>
          </ul>
          <p>
            <strong>Ashers Hub Ltd reserves the right to determine</strong>, at its sole reasonable discretion,
            whether a cancellation qualifies for a full or partial refund.
          </p>

          <h2 className="mt-8 text-xl font-semibold">4. Professional cancellations</h2>
          <p>
            Where a Professional cancels an accepted booking, the Customer will receive a full refund of the booking
            value and all fees paid. Repeated cancellations by a Professional may lead to suspension or removal from
            the platform.
          </p>

          <h2 className="mt-8 text-xl font-semibold">5. Professional protection</h2>
          <p>
            Accepting a booking creates obligations for both parties. Professionals may incur costs and allocate
            working time after accepting a booking. The platform seeks to protect both customers and professionals
            from unreasonable cancellations and applies these rules consistently to both sides.
          </p>

          <h2 className="mt-8 text-xl font-semibold">6. How refunds are issued</h2>
          <p>
            Approved refunds are issued via Stripe to the original payment method and typically arrive within
            5–10 business days. Where a partial refund applies, the retained portion is allocated towards the
            Professional&apos;s compensation and platform administrative costs.
          </p>

          <h2 className="mt-8 text-xl font-semibold">7. Admin review &amp; overrides</h2>
          <p>
            Peopels administrators may approve refunds, issue partial or full refunds, and override cancellation
            penalties where the circumstances warrant — for example in cases of safety, fraud, platform error or
            other exceptional situations.
          </p>

          <h2 className="mt-8 text-xl font-semibold">8. Contact</h2>
          <p>Ashers Hub Ltd · Company No. 17204476 · support@peopels.com</p>
        </article>
      </main>
      <Footer />
    </div>
  );
}
